import {type KeyboardEvent, useCallback, useMemo} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {Controller, useFormContext, UseFormGetValues} from 'react-hook-form';
import {TFunction} from 'i18next';
import {PATTERNS} from '@guestapp/core';
import {useReservation} from 'context/reservation';
import {useGuestGroup, useFirstGuestMustBeAdult} from 'hooks';
import {useSchema} from 'context/guest';
import {i18n} from '../../configs/i18n/i18n';
import {
  getPhoneNumberString,
  POLICE_CODES,
  SelectOptionType,
  STANDARD_FIELD_TYPES,
  Validators,
} from '@guestapp/sdk';
import {COUNTRY_CODES, QUERY_CACHE_KEYS} from 'utils/constants';
import {
  EXTRA_FORM_NAMES,
  FORM_NAMES,
  FormTypes,
  IV_FIELDS,
  SIGNATURE_FIELDS,
} from 'utils/guestForm';
import {useChekinSDK} from 'context/ChekinSDK';
import {
  getDocNumberPattern,
  maxBirthPlaceAddressLength,
  maxLength,
  maxNamesInputLength,
  minLength,
  validateBirthDate,
  validateDocNumberByDocumentType,
  validateExpirationDate,
  validateIssueDate,
  validateResidencePhoneNumber,
  validateGuestMustBeAdult,
  validatePostalCode,
  validateDocNumberUniqueness,
} from 'pages/AddPersonalDataForm/utils';
import Select from 'components/common/Select';
import Datepicker from 'components/common/Datepicker';
import PhoneInput from 'components/common/PhoneInput';
import AdaptiveQueriedInfiniteScrollSelect from 'components/common/QueriedInfiniteScrollSelect';
import {GuestEmailField} from 'components/GuestFormFields';
import {InputController as Input} from '@guestapp/ui';
import {FormFieldWrapper} from 'styled/common';
import {createGetRequiredOrOptionalFieldLabel} from './getRequiredOrOptionalFieldLabel';
import {sortFormNamesByOrder} from './utils/sortFormNamesByOrder';
import {type FormFieldsMapper} from './types';

export const maxSupportNumberInputLength = 9;

const getNameValidationErrorText = (country: string, policeType?: string) => {
  if (country === COUNTRY_CODES.spain && policeType === POLICE_CODES.hospedajes) {
    return i18n.t('guest_names_validation_text.ses_spain');
  } else if (
    [COUNTRY_CODES.spain, COUNTRY_CODES.uae, COUNTRY_CODES.czech].includes(country)
  ) {
    return i18n.t('guest_names_validation_text.only_letters');
  } else {
    return i18n.t('guest_names_validation_text.rest');
  }
};

const filterGenderOptions = (
  options?: SelectOptionType[],
): SelectOptionType[] | undefined => {
  return options?.filter(opt => opt?.value !== 'N');
};

const getNoOptionsMessageForDocTypeField =
  (getValues: UseFormGetValues<FormTypes>, t: TFunction) => () => {
    const isNationalityFilled = Boolean(getValues()[FORM_NAMES.nationality]?.value);

    return !isNationalityFilled ? t('please_fill_nationality_before') : undefined;
  };

const getPhoneFieldLabel = (t: TFunction, policeType?: string) => {
  if (policeType && [POLICE_CODES.uhh, POLICE_CODES.uhh2].includes(policeType)) {
    return t('residence_country_phone');
  }

  return t('phone_number');
};

type UseDefaultFieldsMapperProps = {
  isDisabledField?: boolean;
  housingCountry: string;
  hasAllianzActive?: boolean;
};
export const useDefaultFieldsMapper = ({
  isDisabledField,
  housingCountry,
  hasAllianzActive,
}: UseDefaultFieldsMapperProps) => {
  const {formFields, setHasPhone, isGuestSchemaLoading} = useSchema();
  const {guestGroup} = useGuestGroup();
  const {firstGuestMustBeAdult: shouldFirstGuestBeAdult} = useFirstGuestMustBeAdult();

  const {register, control, formState, setFocus, getValues, watch} =
    useFormContext<FormTypes>();
  const {t} = useTranslation();
  const getRequiredOrOptionalFieldLabel = useMemo(
    () => createGetRequiredOrOptionalFieldLabel(t),
    [t],
  );

  const defaultSecondPhoneDetails = getValues()?.[EXTRA_FORM_NAMES.secondPhoneDetails];

  const {
    getSpanishResidenceCitiesList,
    getCountriesList,
    getDistrictsList,
    getMunicipalitiesList,
    getCitiesList,
    getProvincesList,
  } = useChekinSDK();
  const {data: reservation, housing} = useReservation();

  const errors = useMemo(() => {
    return formState.errors;
  }, [formState]);
  const policeType = housing?.police_account_type;
  const isPoliceActivated = housing?.is_auto_police_registration_enabled;

  const names = useMemo(() => {
    const names: FORM_NAMES[] = [];
    if (Object.values(formFields).length > 0) {
      Object.entries(formFields).forEach(([name, value]) => {
        if (
          value.active &&
          !IV_FIELDS.includes(name) &&
          !SIGNATURE_FIELDS.includes(name as FORM_NAMES)
        ) {
          names.push(name as FORM_NAMES);
        }
      });
      return sortFormNamesByOrder(names, formFields);
    } else {
      return [];
    }
  }, [formFields]);

  const getFormFieldDestinations = useMemo(() => {
    return names.map((formName, index) => {
      const destination = index < names.length - 1 ? names[index + 1] : '';
      return {
        state: formName,
        destination,
      };
    });
  }, [names]);

  const getNextField = useCallback(
    (nextFieldName: FORM_NAMES) => {
      return getFormFieldDestinations.find(item => item.state === nextFieldName);
    },
    [getFormFieldDestinations],
  );

  const handleKeyDown = useCallback(
    (nextFieldName: FORM_NAMES) => (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        const next = getNextField(nextFieldName);
        next && setFocus(next?.destination);
      }
    },
    [setFocus, getNextField],
  );

  const documentType = watch(FORM_NAMES.document_type)?.value;
  const residenceCountry = watch(FORM_NAMES.residence_country)?.value;
  const arrivedFromDistrict = watch(FORM_NAMES.arrived_from_district)?.value;
  const nextDestinationDistrict = watch(FORM_NAMES.next_destination_district)?.value;

  return useMemo<FormFieldsMapper>(
    () => ({
      [FORM_NAMES.name]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(t('name'), required)}
            {...register(FORM_NAMES.name, {
              required,
              pattern: {
                value: format,
                message: getNameValidationErrorText(housingCountry, policeType),
              },
              maxLength: {
                value: maxNamesInputLength,
                message: t('max_length', {length: maxNamesInputLength}),
              },
            })}
            control={control}
            error={errors[FORM_NAMES.name]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={handleKeyDown(FORM_NAMES.name)}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.surname]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(t('surname'), required)}
            {...register(FORM_NAMES.surname, {
              required,
              pattern: {
                value: format,
                message: getNameValidationErrorText(housingCountry, policeType),
              },
              maxLength: {
                value: maxNamesInputLength,
                message: t('max_length', {length: maxNamesInputLength}),
              },
            })}
            control={control}
            error={errors[FORM_NAMES.surname]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={handleKeyDown(FORM_NAMES.surname)}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.second_surname]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(t('second_surname'), required)}
            {...register(FORM_NAMES.second_surname, {
              required,
              pattern: {
                value: format,
                message: getNameValidationErrorText(housingCountry, policeType),
              },
              maxLength: {
                value: maxNamesInputLength,
                message: t('max_length', {length: maxNamesInputLength}),
              },
            })}
            control={control}
            error={errors[FORM_NAMES.second_surname]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={handleKeyDown(FORM_NAMES.second_surname)}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.nationality]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.nationality}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <AdaptiveQueriedInfiniteScrollSelect
                label={getRequiredOrOptionalFieldLabel(t('nationality'), required)}
                placeholder={t('nationality')}
                queryKey={QUERY_CACHE_KEYS.paginatedCountriesList}
                fetcher={(key: string, page: number, searchQuery: string) =>
                  getCountriesList({key, page, searchQuery})
                }
                blockQuery={isDisabledField}
                disabled={isDisabledField}
                onKeyDown={handleKeyDown(FORM_NAMES.nationality)}
                error={error?.message}
                openMenuOnFocus={false}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.birth_place_country]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.birth_place_country}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <AdaptiveQueriedInfiniteScrollSelect
                label={getRequiredOrOptionalFieldLabel(
                  t('birth_place_country'),
                  required,
                )}
                placeholder={t('birth_place_country')}
                queryKey={QUERY_CACHE_KEYS.paginatedCountriesList}
                fetcher={(key: string, page: number, searchQuery: string) =>
                  getCountriesList({key, page, searchQuery})
                }
                blockQuery={isDisabledField}
                disabled={isDisabledField}
                error={error?.message}
                onKeyDown={handleKeyDown(FORM_NAMES.birth_place_country)}
                openMenuOnFocus={false}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.birth_place_city]: ({required, format}) => {
        switch (formFields?.[FORM_NAMES.birth_place_city]?.type) {
          case STANDARD_FIELD_TYPES.select:
            return (
              <FormFieldWrapper>
                <Controller
                  name={FORM_NAMES.birth_place_city}
                  rules={{required}}
                  control={control}
                  render={({field: {value, ...restField}, fieldState: {error}}) => (
                    <AdaptiveQueriedInfiniteScrollSelect
                      label={getRequiredOrOptionalFieldLabel(
                        t('city_of_birth'),
                        required,
                      )}
                      placeholder={t('city_of_birth')}
                      queryKey={[QUERY_CACHE_KEYS.paginatedCitiesList]}
                      fetcher={(key: string, page: number, searchQuery: string) =>
                        getCitiesList({
                          key,
                          page,
                          searchQuery,
                          countryCode: housingCountry,
                        })
                      }
                      blockQuery={isDisabledField}
                      disabled={isDisabledField}
                      value={value as SelectOptionType}
                      error={error?.message}
                      openMenuOnFocus={false}
                      {...restField}
                    />
                  )}
                />
              </FormFieldWrapper>
            );
          default:
            return (
              <FormFieldWrapper>
                <Input
                  label={getRequiredOrOptionalFieldLabel(t('city_of_birth'), required)}
                  {...register(FORM_NAMES.birth_place_city, {
                    required,
                    pattern: {
                      value: format,
                      message: t('cant_contain_number_and_symbols'),
                    },
                    maxLength: {
                      value: maxNamesInputLength,
                      message: t('max_length', {length: maxNamesInputLength}),
                    },
                  })}
                  control={control}
                  error={errors[FORM_NAMES.birth_place_city]?.message}
                  disabled={isDisabledField}
                  autoCorrect="off"
                  spellCheck={false}
                  onKeyDown={handleKeyDown(FORM_NAMES.birth_place_city)}
                />
              </FormFieldWrapper>
            );
        }
      },
      [FORM_NAMES.birth_place_address]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(t('birth_place_address'), required)}
            {...register(FORM_NAMES.birth_place_address, {
              required,
              pattern: {
                value: format,
                message: t('cant_contain_number_and_symbols'),
              },
              maxLength: {
                value: maxBirthPlaceAddressLength,
                message: t('max_length', {length: maxBirthPlaceAddressLength}),
              },
            })}
            control={control}
            error={errors[FORM_NAMES.birth_place_address]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={handleKeyDown(FORM_NAMES.birth_place_address)}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.tax_exemption]: ({
        required,
        options = formFields?.[FORM_NAMES.tax_exemption]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.tax_exemption}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('tax_exemption')}
                label={getRequiredOrOptionalFieldLabel(t('tax_exemption'), required)}
                options={options}
                error={error?.message}
                disabled={isDisabledField}
                loading={isDisabledField}
                onKeyDown={handleKeyDown(FORM_NAMES.tax_exemption)}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.email]: ({required, format}) => (
        <FormFieldWrapper>
          <GuestEmailField
            name={FORM_NAMES.email}
            required={required}
            format={format}
            label={getRequiredOrOptionalFieldLabel(t('email'), required)}
            onKeyDown={handleKeyDown(FORM_NAMES.email)}
            disabled={isDisabledField}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.phone]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.phone}
            rules={{
              required,
              validate: {
                length: validateResidencePhoneNumber(t, policeType),
                full: Validators.phoneValidateService,
              },
            }}
            control={control}
            render={({field: {onBlur, onChange, ...restField}, fieldState: {error}}) => (
              <PhoneInput
                label={getRequiredOrOptionalFieldLabel(
                  getPhoneFieldLabel(t, policeType),
                  required,
                )}
                error={error?.message}
                onBlur={() => {
                  const phoneValue = getValues()[FORM_NAMES.phone];
                  const hasNumber = !!getPhoneNumberString(phoneValue);
                  setHasPhone?.(hasNumber);
                  onBlur();
                }}
                onChange={(_, __, details) => onChange(details)}
                disabled={isDisabledField}
                onKeyDown={handleKeyDown(FORM_NAMES.phone)}
                {...restField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.second_phone]: ({required, format}) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.second_phone}
            rules={{
              required,
              pattern: {
                value: format,
                message: t('invalid_phone_number'),
              },
              maxLength: {
                value: 16,
                message: t('invalid_phone_number'),
              },
              minLength: {
                value: 12,
                message: t('invalid_phone_number'),
              },
            }}
            control={control}
            render={({field: {onBlur, ...restField}, fieldState: {error}}) => (
              <PhoneInput
                defaultCode="+971"
                defaultInputValue={defaultSecondPhoneDetails?.number}
                label={getRequiredOrOptionalFieldLabel(t('uae_phone_number'), required)}
                error={error?.message}
                disabled={isDisabledField}
                tooltipContent={t('uae_phone_number_tooltip')}
                codeReadOnly
                {...restField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.purpose_of_stay]: ({
        required,
        options = formFields?.[FORM_NAMES.purpose_of_stay]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.purpose_of_stay}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('purpose_of_stay')}
                label={getRequiredOrOptionalFieldLabel(t('purpose_of_stay'), required)}
                options={options}
                loading={isGuestSchemaLoading}
                error={error?.message}
                disabled={isDisabledField}
                onKeyDown={handleKeyDown(FORM_NAMES.purpose_of_stay)}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.document_type]: ({
        required,
        options = formFields?.[FORM_NAMES.document_type]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.document_type}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('document_type')}
                options={options}
                label={getRequiredOrOptionalFieldLabel(t('document_type'), required)}
                error={error?.message}
                disabled={isDisabledField}
                onKeyDown={handleKeyDown(FORM_NAMES.document_type)}
                noOptionsMessage={getNoOptionsMessageForDocTypeField(getValues, t)}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.document_number]: ({required}) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(t('document_number'), required)}
            {...register(FORM_NAMES.document_number, {
              required,
              pattern: {
                value: getDocNumberPattern(housingCountry, isPoliceActivated),
                message: t('only_basic_letters_are_allowed'),
              },
              maxLength: {
                value: maxLength,
                message: t('max_length', {length: maxLength}),
              },
              minLength: {
                value: minLength,
                message: t('min_length', {length: minLength}),
              },
              validate: {
                byDocumentType: value =>
                  validateDocNumberByDocumentType(value, documentType),
                uniqueness: value => validateDocNumberUniqueness(value, guestGroup),
              },
            })}
            control={control}
            error={errors[FORM_NAMES.document_number]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={handleKeyDown(FORM_NAMES.document_number)}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.document_issue_date]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.document_issue_date}
            rules={{
              required,
              validate: value => {
                if (!value) return true;

                const birthDate = getValues()[FORM_NAMES.birth_date];
                const expirationDate = getValues()[FORM_NAMES.document_expiration_date];
                return validateIssueDate(value, birthDate, expirationDate);
              },
            }}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Datepicker
                withTooltip
                tooltipContent={
                  <Trans i18nKey="date_when_the_doc_created">
                    Date when the document was created. (It is <b>not</b> the expiration
                    date)
                  </Trans>
                }
                error={error?.message}
                disabled={isDisabledField}
                label={getRequiredOrOptionalFieldLabel(t('date_of_issue'), required)}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.document_expiration_date]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.document_expiration_date}
            rules={{
              required,
              validate: value => {
                if (!value) return true;

                const birthDate = getValues()[FORM_NAMES.birth_date];
                const issueDate = getValues()[FORM_NAMES.document_issue_date];
                return validateExpirationDate({
                  expirationDate: value,
                  birthDate,
                  issueDate,
                  checkInDate: reservation?.check_in_date,
                });
              },
            }}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Datepicker
                error={error?.message}
                disabled={isDisabledField}
                label={getRequiredOrOptionalFieldLabel(
                  t('document_expiration_date'),
                  required,
                )}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.birth_date]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.birth_date}
            rules={{
              required,
              validate: {
                validateGuestMustBeAdult: value => {
                  if (!shouldFirstGuestBeAdult) {
                    return true;
                  }
                  return validateGuestMustBeAdult(value);
                },
                common: value => {
                  if (!value) {
                    return true;
                  }
                  const docDateOfIssue = getValues()[FORM_NAMES.document_issue_date];
                  return validateBirthDate(value, docDateOfIssue);
                },
              },
            }}
            control={control}
            render={({field, fieldState: {error}}) => {
              return (
                <Datepicker
                  error={error?.message}
                  label={getRequiredOrOptionalFieldLabel(t('birth_date'), required)}
                  disabled={isDisabledField}
                  onHandleKeyDown={handleKeyDown(FORM_NAMES.birth_date)}
                  {...field}
                />
              );
            }}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.gender]: ({
        required,
        options = formFields?.[FORM_NAMES.gender]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.gender}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('sex')}
                label={getRequiredOrOptionalFieldLabel(t('sex'), required)}
                options={filterGenderOptions(options)}
                error={error?.message}
                disabled={isDisabledField}
                onKeyDown={handleKeyDown(FORM_NAMES.gender)}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.citizenship]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.citizenship}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <AdaptiveQueriedInfiniteScrollSelect
                label={getRequiredOrOptionalFieldLabel(t('citizenship'), required)}
                placeholder={t('citizenship')}
                queryKey={QUERY_CACHE_KEYS.paginatedCountriesList}
                fetcher={(key: string, page: number, searchQuery: string) =>
                  getCountriesList({key, page, searchQuery})
                }
                blockQuery={isDisabledField}
                disabled={isDisabledField}
                error={error?.message}
                openMenuOnFocus={false}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.document_expedition_country]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.document_expedition_country}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <AdaptiveQueriedInfiniteScrollSelect
                label={getRequiredOrOptionalFieldLabel(t('country_of_issue'), required)}
                placeholder={t('country_of_issue')}
                queryKey={QUERY_CACHE_KEYS.paginatedCountriesList}
                fetcher={(key: string, page: number, searchQuery: string) =>
                  getCountriesList({key, page, searchQuery})
                }
                blockQuery={isDisabledField}
                disabled={isDisabledField}
                error={error?.message}
                openMenuOnFocus={false}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.document_expedition_city]: ({required, format}) => {
        switch (formFields?.[FORM_NAMES.document_expedition_city]?.type) {
          case STANDARD_FIELD_TYPES.select:
            return (
              <FormFieldWrapper>
                <Controller
                  name={FORM_NAMES.document_expedition_city}
                  rules={{required}}
                  control={control}
                  render={({field: {value, ...restField}, fieldState: {error}}) => (
                    <AdaptiveQueriedInfiniteScrollSelect
                      label={getRequiredOrOptionalFieldLabel(
                        t('city_of_issue'),
                        required,
                      )}
                      placeholder={t('city_of_issue')}
                      queryKey={QUERY_CACHE_KEYS.paginatedCitiesList}
                      fetcher={(key: string, page: number, searchQuery: string) =>
                        getCitiesList({
                          key,
                          page,
                          searchQuery,
                          countryCode: housingCountry,
                        })
                      }
                      blockQuery={isDisabledField}
                      disabled={isDisabledField}
                      value={value as SelectOptionType}
                      error={error?.message}
                      openMenuOnFocus={false}
                      {...restField}
                    />
                  )}
                />
              </FormFieldWrapper>
            );
          default:
            return (
              <FormFieldWrapper>
                <Input
                  label={getRequiredOrOptionalFieldLabel(t('city_of_issue'), required)}
                  {...register(FORM_NAMES.document_expedition_city, {
                    required,
                    pattern: {
                      value: format,
                      message: t('cant_contain_number_and_symbols'),
                    },
                    maxLength: {
                      value: maxNamesInputLength,
                      message: t('max_length', {length: maxNamesInputLength}),
                    },
                  })}
                  control={control}
                  disabled={isDisabledField}
                  autoCorrect="off"
                  spellCheck={false}
                />
              </FormFieldWrapper>
            );
        }
      },
      [FORM_NAMES.full_tourist_tax]: ({required}) => (
        <FormFieldWrapper>
          <Input
            {...register(FORM_NAMES.full_tourist_tax, {
              required,
            })}
            control={control}
            label={getRequiredOrOptionalFieldLabel(t('full_tourist_tax'), required)}
            inputMode="numeric"
            error={errors[FORM_NAMES.full_tourist_tax]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.residence_country]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.residence_country}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <AdaptiveQueriedInfiniteScrollSelect
                label={getRequiredOrOptionalFieldLabel(t('residence_country'), required)}
                placeholder={t('residence_country')}
                queryKey={QUERY_CACHE_KEYS.paginatedCountriesList}
                fetcher={(key: string, page: number, searchQuery: string) =>
                  getCountriesList({key, page, searchQuery})
                }
                blockQuery={isDisabledField}
                disabled={isDisabledField || hasAllianzActive}
                error={error?.message}
                openMenuOnFocus={false}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.residence_community]: ({
        required,
        options = formFields?.[FORM_NAMES.residence_community]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.residence_community}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                label={getRequiredOrOptionalFieldLabel(t('residence_state'), required)}
                placeholder={t('residence_state')}
                options={options}
                disabled={isDisabledField}
                error={error?.message}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.residence_province]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.residence_province}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <AdaptiveQueriedInfiniteScrollSelect
                label={getRequiredOrOptionalFieldLabel(t('residence_province'), required)}
                placeholder={t('select')}
                queryKey={[QUERY_CACHE_KEYS.paginatedProvincesList, housingCountry]}
                fetcher={(key: string, page: number, searchQuery: string) =>
                  getProvincesList({key, page, searchQuery, countryCode: housingCountry})
                }
                blockQuery={isDisabledField}
                disabled={isDisabledField}
                error={error?.message}
                openMenuOnFocus={false}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.residence_city]: ({required, format}) => {
        const isSelectType =
          formFields?.[FORM_NAMES.residence_city]?.type === STANDARD_FIELD_TYPES.select;

        if (isSelectType) {
          const isSpain = residenceCountry === COUNTRY_CODES.spain;
          const label = isSpain ? t('city') : t('residence_city');
          const queryKey = isSpain
            ? [QUERY_CACHE_KEYS.paginatedSpanishResidenceCities]
            : [QUERY_CACHE_KEYS.paginatedCitiesList, residenceCountry];

          return (
            <FormFieldWrapper>
              <Controller
                name={FORM_NAMES.residence_city}
                rules={{required}}
                control={control}
                render={({field: {value, ...field}, fieldState: {error}}) => (
                  <AdaptiveQueriedInfiniteScrollSelect
                    label={getRequiredOrOptionalFieldLabel(label, required)}
                    placeholder={label}
                    queryKey={queryKey}
                    fetcher={(key: string, page: number, searchQuery: string) =>
                      isSpain
                        ? getSpanishResidenceCitiesList({key, page, searchQuery})
                        : getCitiesList({
                            key,
                            page,
                            searchQuery,
                            countryCode: residenceCountry || '',
                          })
                    }
                    blockQuery={isDisabledField}
                    disabled={isDisabledField}
                    error={error?.message}
                    openMenuOnFocus={false}
                    value={value as SelectOptionType}
                    {...field}
                  />
                )}
              />
            </FormFieldWrapper>
          );
        }

        return (
          <FormFieldWrapper>
            <Input
              label={getRequiredOrOptionalFieldLabel(t('residence_city'), required)}
              {...register(FORM_NAMES.residence_city, {
                required,
                pattern: {
                  value: format,
                  message: t('cant_contain_number_and_symbols'),
                },
                maxLength: {
                  value: maxNamesInputLength,
                  message: t('max_length', {length: maxNamesInputLength}),
                },
              })}
              control={control}
              error={errors[FORM_NAMES.residence_city]?.message}
              disabled={isDisabledField}
              autoCorrect="off"
              spellCheck={false}
            />
          </FormFieldWrapper>
        );
      },
      [FORM_NAMES.arrived_from_country]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.arrived_from_country}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <AdaptiveQueriedInfiniteScrollSelect
                label={getRequiredOrOptionalFieldLabel(
                  t('arrived_from_country'),
                  required,
                )}
                placeholder={t('arrived_from_country')}
                queryKey={QUERY_CACHE_KEYS.paginatedCountriesList}
                fetcher={(key: string, page: number, searchQuery: string) =>
                  getCountriesList({key, page, searchQuery})
                }
                blockQuery={isDisabledField}
                disabled={isDisabledField}
                error={error?.message}
                openMenuOnFocus={false}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.arrived_from_district]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.arrived_from_district}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <AdaptiveQueriedInfiniteScrollSelect
                label={getRequiredOrOptionalFieldLabel(
                  t('arrived_from_district'),
                  required,
                )}
                placeholder={t('arrived_from_district')}
                queryKey={QUERY_CACHE_KEYS.paginatedDistrictsList}
                fetcher={(key: string, page: number, searchQuery: string) =>
                  getDistrictsList({key, page, searchQuery, countryCode: housingCountry})
                }
                blockQuery={isDisabledField}
                disabled={isDisabledField}
                error={error?.message}
                openMenuOnFocus={false}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.arrived_from_municipality]: ({required, format}) => {
        switch (formFields?.[FORM_NAMES.arrived_from_municipality]?.type) {
          case STANDARD_FIELD_TYPES.select:
            return (
              <FormFieldWrapper>
                <Controller
                  name={FORM_NAMES.arrived_from_municipality}
                  rules={{required}}
                  control={control}
                  render={({field: {value, ...restField}, fieldState: {error}}) => (
                    <AdaptiveQueriedInfiniteScrollSelect
                      label={getRequiredOrOptionalFieldLabel(
                        t('arrived_from_municipality'),
                        required,
                      )}
                      placeholder={t('arrived_from_municipality')}
                      queryKey={[
                        QUERY_CACHE_KEYS.paginatedMunicipalitiesList,
                        arrivedFromDistrict,
                      ]}
                      fetcher={(key: string, page: number, searchQuery: string) =>
                        getMunicipalitiesList({
                          key,
                          page,
                          searchQuery,
                          countryCode: housingCountry,
                          district: arrivedFromDistrict,
                        })
                      }
                      blockQuery={isDisabledField}
                      disabled={isDisabledField}
                      value={value as SelectOptionType}
                      error={error?.message}
                      openMenuOnFocus={false}
                      {...restField}
                    />
                  )}
                />
              </FormFieldWrapper>
            );
          default:
            return (
              <FormFieldWrapper>
                <Input
                  label={getRequiredOrOptionalFieldLabel(
                    t('arrived_from_municipality'),
                    required,
                  )}
                  {...register(FORM_NAMES.arrived_from_municipality, {
                    required,
                    pattern: {
                      value: format,
                      message: t('cant_contain_number_and_symbols'),
                    },
                    maxLength: {
                      value: maxNamesInputLength,
                      message: t('max_length', {length: maxNamesInputLength}),
                    },
                  })}
                  control={control}
                  error={errors[FORM_NAMES.arrived_from_municipality]?.message}
                  disabled={isDisabledField}
                  autoCorrect="off"
                  spellCheck={false}
                  onKeyDown={handleKeyDown(FORM_NAMES.arrived_from_municipality)}
                />
              </FormFieldWrapper>
            );
        }
      },
      [FORM_NAMES.next_destination_country]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.next_destination_country}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <AdaptiveQueriedInfiniteScrollSelect
                label={getRequiredOrOptionalFieldLabel(
                  t('next_destination_country'),
                  required,
                )}
                placeholder={t('next_destination_country')}
                queryKey={QUERY_CACHE_KEYS.paginatedCountriesList}
                fetcher={(key: string, page: number, searchQuery: string) =>
                  getCountriesList({key, page, searchQuery})
                }
                blockQuery={isDisabledField}
                disabled={isDisabledField}
                error={error?.message}
                openMenuOnFocus={false}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.next_destination_district]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.next_destination_district}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <AdaptiveQueriedInfiniteScrollSelect
                label={getRequiredOrOptionalFieldLabel(
                  t('next_destination_district'),
                  required,
                )}
                placeholder={t('next_destination_district')}
                queryKey={QUERY_CACHE_KEYS.paginatedDistrictsList}
                fetcher={(key: string, page: number, searchQuery: string) =>
                  getDistrictsList({key, page, searchQuery, countryCode: housingCountry})
                }
                blockQuery={isDisabledField}
                disabled={isDisabledField}
                error={error?.message}
                openMenuOnFocus={false}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.next_destination_municipality]: ({required, format}) => {
        const showSelectFormat =
          formFields?.[FORM_NAMES.next_destination_municipality]?.type ===
            STANDARD_FIELD_TYPES.select && Boolean(nextDestinationDistrict);

        if (showSelectFormat) {
          return (
            <FormFieldWrapper>
              <Controller
                name={FORM_NAMES.next_destination_municipality}
                rules={{required}}
                control={control}
                render={({field, fieldState: {error}}) => (
                  <AdaptiveQueriedInfiniteScrollSelect
                    placeholder={t('next_destination_municipality')}
                    label={getRequiredOrOptionalFieldLabel(
                      t('next_destination_municipality'),
                      required,
                    )}
                    queryKey={[
                      QUERY_CACHE_KEYS.paginatedMunicipalitiesList,
                      nextDestinationDistrict,
                    ]}
                    fetcher={(key: string, page: number, searchQuery: string) =>
                      getMunicipalitiesList({
                        key,
                        page,
                        searchQuery,
                        countryCode: housingCountry,
                        district: nextDestinationDistrict,
                      })
                    }
                    blockQuery={isDisabledField}
                    disabled={isDisabledField}
                    error={error?.message}
                    openMenuOnFocus={false}
                    {...field}
                  />
                )}
              />
            </FormFieldWrapper>
          );
        }
        return (
          <FormFieldWrapper>
            <Input
              label={getRequiredOrOptionalFieldLabel(
                t('next_destination_municipality'),
                required,
              )}
              {...register(FORM_NAMES.next_destination_municipality, {
                required,
                pattern: {
                  value: format,
                  message: t('cant_contain_number_and_symbols'),
                },
                maxLength: {
                  value: maxNamesInputLength,
                  message: t('max_length', {length: maxNamesInputLength}),
                },
              })}
              control={control}
              error={errors[FORM_NAMES.next_destination_municipality]?.message}
              disabled={isDisabledField}
              autoCorrect="off"
              spellCheck={false}
              onKeyDown={handleKeyDown(FORM_NAMES.next_destination_municipality)}
            />
          </FormFieldWrapper>
        );
      },
      [FORM_NAMES.residence_address]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            {...register(FORM_NAMES.residence_address, {
              pattern: {
                value: format,
                message: getNameValidationErrorText(housingCountry, policeType),
              },
              required,
            })}
            control={control}
            label={getRequiredOrOptionalFieldLabel(t('residence_address'), required)}
            error={errors[FORM_NAMES.residence_address]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.residence_postal_code]: ({required}) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(t('residence_postal_code'), required)}
            {...register(FORM_NAMES.residence_postal_code, {
              required,
              validate: validatePostalCode(t, residenceCountry),
            })}
            control={control}
            error={errors[FORM_NAMES.residence_postal_code]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.next_destination_address]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            {...register(FORM_NAMES.next_destination_address, {
              pattern: {
                value: format,
                message: t('cant_contain_number_and_symbols'),
              },
              required,
            })}
            control={control}
            label={getRequiredOrOptionalFieldLabel(
              t('next_destination_address'),
              required,
            )}
            error={errors[FORM_NAMES.next_destination_address]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.fiscal_code]: ({required}) => (
        <FormFieldWrapper>
          <Input
            {...register(FORM_NAMES.fiscal_code, {
              required,
              pattern: {
                value: PATTERNS.fiscalCode,
                message: t('invalid_fiscal_code'),
              },
            })}
            control={control}
            label={getRequiredOrOptionalFieldLabel(t('fiscal_code'), required)}
            error={errors[FORM_NAMES.fiscal_code]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.document_support_number]: ({required}) => (
        <FormFieldWrapper>
          <Input
            {...register(FORM_NAMES.document_support_number, {
              required,
              maxLength: {
                value: maxSupportNumberInputLength,
                message: t('max_length', {length: maxSupportNumberInputLength}),
              },
            })}
            control={control}
            label={getRequiredOrOptionalFieldLabel(
              t('document_support_number'),
              required,
            )}
            error={errors[FORM_NAMES.document_support_number]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.visa_number]: ({required}) => (
        <FormFieldWrapper>
          <Input
            {...register(FORM_NAMES.visa_number, {required})}
            control={control}
            label={getRequiredOrOptionalFieldLabel(t('visa_number'), required)}
            error={errors[FORM_NAMES.visa_number]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.kinship_relationship]: ({
        required,
        options = formFields?.[FORM_NAMES.kinship_relationship]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.kinship_relationship}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                label={getRequiredOrOptionalFieldLabel(t('relationship'), required)}
                placeholder={getRequiredOrOptionalFieldLabel(t('relationship'), required)}
                options={options}
                error={error?.message}
                disabled={isDisabledField}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.external_category_id]: ({
        required,
        options = formFields?.[FORM_NAMES.external_category_id]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.external_category_id}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                label={getRequiredOrOptionalFieldLabel(t('category'), required)}
                placeholder={getRequiredOrOptionalFieldLabel(t('category'), required)}
                options={options}
                error={error?.message}
                disabled={isDisabledField}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
    }),
    [
      getRequiredOrOptionalFieldLabel,
      t,
      register,
      control,
      errors,
      isDisabledField,
      handleKeyDown,
      getCountriesList,
      formFields,
      getCitiesList,
      housingCountry,
      policeType,
      getValues,
      setHasPhone,
      defaultSecondPhoneDetails?.number,
      isGuestSchemaLoading,
      isPoliceActivated,
      documentType,
      reservation?.check_in_date,
      hasAllianzActive,
      getProvincesList,
      residenceCountry,
      getSpanishResidenceCitiesList,
      getDistrictsList,
      arrivedFromDistrict,
      getMunicipalitiesList,
      nextDestinationDistrict,
      shouldFirstGuestBeAdult,
      guestGroup,
    ],
  );
};
